import { Container, Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import './MainNavbar.scss';
import { ReactComponent as ThinnectSmallLogo } from './../assets/icons/NN_logo.svg';
import { useTranslation } from 'react-i18next';

type Props = {
	windowSize: { width: number | undefined; height: number | undefined };
	isLoggedIn: boolean;
	tenancyStatus: boolean;
	withoutToken: boolean;
	multipleTenancy: boolean;
	isAdmin: boolean;
	emitClientSiteChange: () => void;
	emitLogout: () => void;
};

const MainNavbar = ({
	windowSize,
	isLoggedIn,
	tenancyStatus,
	withoutToken,
	multipleTenancy,
	isAdmin,
	emitClientSiteChange,
	emitLogout,
}: Props) => {
	const { t, i18n } = useTranslation();

	const [toggleNavbar, setToggleNavbar] = useState<boolean>(false);

	useEffect(() => {
		if (windowSize.width && windowSize.width > 768) {
			document.body.style.overflowY = '';
			setToggleNavbar(false);
		}
	}, [windowSize]);

	function toggleNavbarStatus(status: boolean): void {
		if (status !== toggleNavbar) {
			document.body.style.overflowY = !toggleNavbar ? 'hidden' : '';
			setToggleNavbar(status);
		}
	}

	async function changeLanguage(language: string): Promise<void> {
		if (language !== i18n.language) {
			await i18n.changeLanguage(language);
		}
	}

	function logout() {
		toggleNavbarStatus(false);
		emitLogout();
	}

	function changeClient() {
		toggleNavbarStatus(false);
		emitClientSiteChange();
	}

	function languageSelection(loginStatus: boolean): JSX.Element {
		return (
			<NavDropdown
				title={t('common.current_language')}
				menuVariant={loginStatus ? 'light' : 'dark'}
				className={!loginStatus ? 'navbar-language-dropdown' : ''}
				id="nav-dropdown-language"
				align="end"
			>
				<NavDropdown.Item onClick={() => changeLanguage('ee')}>
					<div>
						<span className="fw-bolder">Eesti </span>
						<span className="fw-normal">EE</span>
					</div>
				</NavDropdown.Item>
				<NavDropdown.Item onClick={() => changeLanguage('en')}>
					<div>
						<span className="fw-bolder">English </span>
						<span className="fw-normal">EN</span>
					</div>
				</NavDropdown.Item>
				<NavDropdown.Item onClick={() => changeLanguage('fr')}>
					<div>
						<span className="fw-bolder">Français </span>
						<span className="fw-normal">FR</span>
					</div>
				</NavDropdown.Item>
			</NavDropdown>
		);
	}

	return (
		<Navbar expand="md" variant="light" expanded={toggleNavbar} collapseOnSelect={true}>
			<Container fluid>
				<Navbar.Brand>
					<ThinnectSmallLogo className="thinnect-small-logo" />
				</Navbar.Brand>
				{!isLoggedIn && languageSelection(isLoggedIn)}
				{isLoggedIn && (
					<Navbar.Toggle
						aria-controls="navbar-nav"
						onClick={() => toggleNavbarStatus(!toggleNavbar)}
					/>
				)}
				{isLoggedIn && (
					<Navbar.Collapse id="navbar-nav">
						<Nav className="ms-md-auto">
							{tenancyStatus && (
								<>
									<Nav.Link onClick={() => toggleNavbarStatus(false)} as={NavLink} to="/dashboard">
										{t('navbar.menu_dashboard')}
									</Nav.Link>
									<Nav.Link onClick={() => toggleNavbarStatus(false)} as={NavLink} to="/analyze">
										{t('navbar.menu_analysis')}
									</Nav.Link>
									<Nav.Link
										onClick={() => toggleNavbarStatus(false)}
										as={NavLink}
										to="/notification"
									>
										{t('navbar.menu_notifications')}
									</Nav.Link>
									<Nav.Link onClick={() => toggleNavbarStatus(false)} as={NavLink} to="/report">
										{t('navbar.menu_report')}
									</Nav.Link>
									<Nav.Link onClick={() => toggleNavbarStatus(false)} as={NavLink} to="/info">
										{t('navbar.menu_info')}
									</Nav.Link>
									{!withoutToken && multipleTenancy && (
										<Nav.Link onClick={() => changeClient()}>{t('navbar.menu_tenancy')}</Nav.Link>
									)}
								</>
							)}
							{!withoutToken && isAdmin && (
								<NavDropdown title={t('navbar.menu_admin')} id="admin-nav-dropdown">
									<NavDropdown.Item href="admin">{t('navbar.menu_admin_panel')}</NavDropdown.Item>
									<NavDropdown.Divider />
									<NavDropdown.Item href="airpurity-commissioning">
										{t('navbar.menu_admin_tempsense')}
									</NavDropdown.Item>
								</NavDropdown>
							)}
							{languageSelection(isLoggedIn)}
							<Nav.Link onClick={() => logout()}>{t('navbar.menu_log_out')}</Nav.Link>
						</Nav>
					</Navbar.Collapse>
				)}
			</Container>
		</Navbar>
	);
};

export default MainNavbar;
